/**
 * useTaskInboxRead — RW-2867: the server half of the one task inbox
 * (dealsApi `taskInbox`: deal rows + onboarding rows). My Tasks, Home's queue
 * and the nav badge all read it, so they agree.
 *
 *   live    — refetch on the deals signal (My Tasks): a burst refetches once,
 *             SIGNAL_DEBOUNCE_MS after it goes quiet, never later than
 *             SIGNAL_MAX_WAIT_MS after its first signal (RW-2858 B7).
 *   pollMs  — refetch every pollMs, on window focus (30 s guard) and when a
 *             screen announces a change (announceTasksChanged) — the badge.
 *
 * One read at a time: a refetch asked for mid-read queues exactly one more,
 * and nothing lands after the caller has gone (RW-2858). A kind the server
 * couldn't read (`failed`) keeps the rows it had, and errors.deals /
 * errors.onboarding say so — a failure is never shown as "nothing to do".
 * A read: no rate limit on the callable, no throttle here beyond the focus
 * guard.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { dealsService } from '../services/deals';
import { subscribe } from '../services/data';
import { useTenant } from '../contexts/TenantContext';

import { TASKS_CHANGED_EVENT, announceTasksChanged } from '../lib/tasksChanged';

export { TASKS_CHANGED_EVENT, announceTasksChanged };

const SIGNAL_DEBOUNCE_MS = 1500;
const SIGNAL_MAX_WAIT_MS = 5000;
const FOCUS_GUARD_MS = 30000;

export default function useTaskInboxRead({ enabled = true, live = false, pollMs = 0, onError = null } = {}) {
  const { tenantId } = useTenant();
  const [dealRows, setDealRows] = useState([]);
  const [onboardingRows, setOnboardingRows] = useState([]);
  const [loading, setLoading] = useState(!!enabled);
  const [errors, setErrors] = useState({ deals: null, onboarding: null });
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const reads = useRef({ inFlight: false, again: false, live: true, last: 0 });
  useEffect(() => {
    const r = reads.current;
    r.live = true;
    return () => { r.live = false; };
  }, []);

  const refetch = useCallback(async () => {
    const r = reads.current;
    if (r.inFlight) { r.again = true; return; }
    r.inFlight = true;
    try {
      do {
        r.again = false;
        r.last = Date.now();
        try {
          const res = await dealsService.taskInbox();
          if (r.live) {
            const failed = Array.isArray(res && res.failed) ? res.failed : [];
            if (!failed.includes('deals')) setDealRows(Array.isArray(res && res.tasks) ? res.tasks : []);
            if (!failed.includes('onboarding')) setOnboardingRows(Array.isArray(res && res.onboarding) ? res.onboarding : []);
            setErrors({
              deals: failed.includes('deals') ? new Error('deal tasks unavailable') : null,
              onboarding: failed.includes('onboarding') ? new Error('onboarding tasks unavailable') : null,
            });
            if (failed.length && onErrorRef.current) onErrorRef.current();
          }
        } catch (e) {
          // the rows stay as they were — [] on a first-load failure, which
          // must read as a failure, never as an empty list (RW-2436)
          if (r.live) {
            setErrors({ deals: e, onboarding: e });
            if (onErrorRef.current) onErrorRef.current(e);
          }
        }
        if (r.live) setLoading(false);
      } while (r.again && r.live);
    } finally {
      r.inFlight = false;
    }
  }, []);
  const retry = useCallback(() => { setLoading(true); refetch(); }, [refetch]);

  useEffect(() => {
    if (!enabled) { setLoading(false); return; }
    refetch();
  }, [enabled, refetch]);

  // live: the deals signal (every deal mutation bumps it)
  useEffect(() => {
    if (!enabled || !live || !tenantId) return undefined;
    let first = true;
    let timer = null;
    let burstStart = 0;
    const unsubscribe = subscribe(`signals/${tenantId}__deals`, () => {
      if (first) { first = false; return; } // the doc as it is now — the first load covers it
      const now = Date.now();
      if (!timer) burstStart = now;
      clearTimeout(timer);
      const wait = Math.max(0, Math.min(SIGNAL_DEBOUNCE_MS, burstStart + SIGNAL_MAX_WAIT_MS - now));
      timer = setTimeout(() => { timer = null; refetch(); }, wait);
    }, () => {});
    return () => { clearTimeout(timer); if (unsubscribe) unsubscribe(); };
  }, [enabled, live, tenantId, refetch]);

  // pollMs: the badge
  useEffect(() => {
    if (!enabled || !pollMs) return undefined;
    const onFocus = () => { if (Date.now() - reads.current.last >= FOCUS_GUARD_MS) refetch(); };
    const onChanged = () => refetch();
    const iv = setInterval(refetch, pollMs);
    window.addEventListener('focus', onFocus);
    window.addEventListener(TASKS_CHANGED_EVENT, onChanged);
    return () => {
      clearInterval(iv);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(TASKS_CHANGED_EVENT, onChanged);
    };
  }, [enabled, pollMs, refetch]);

  return { dealRows, setDealRows, onboardingRows, setOnboardingRows, loading, errors, refetch, retry };
}
